import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { selectPushPublicKey } from "src/store/common"
import {
  currentSubscription,
  disablePush,
  enablePush,
  pushSupport,
  resendLanguage,
} from "src/utils/pushNotifications"

import "./InstallSettings.css"

/* "Tell me when my round is ready", in the profile pop-up beside the install row (owner,
 * 2026-10-02). A switch rather than a prompt on the board: the browser's permission dialog must
 * come from a press, and a player asked on arrival says no and is never asked again.
 *
 * Says nothing at all where it cannot work — no worker (a dev server), no key from the server, no
 * Push API (Safari outside the installed app) — for the reason the install row gives: a switch
 * with nothing behind it is worse than silence. A browser-level refusal is said once, since only
 * the browser's own settings can undo it. */
const PushSettings = () => {
  const { t, i18n } = useTranslation()
  const publicKey = useSelector(selectPushPublicKey)
  const support = pushSupport(publicKey)
  const [subscribed, setSubscribed] = useState(null)
  const [busy, setBusy] = useState(false)
  const language = useRef(i18n.language)

  useEffect(() => {
    if (support !== "available") return undefined
    let alive = true
    currentSubscription()
      .then((subscription) => {
        if (!alive) return
        // No worker: nothing to switch on, so nothing is shown (`undefined`, not `null` for "off").
        if (subscription === undefined) return setSubscribed(null)
        setSubscribed(Boolean(subscription))
        // The browser's subscription is what the switch reads, but the server files it by account:
        // on a shared phone the second account to sign in would read "on" while the rounds still
        // went to the first. Sending it once on mount re-homes it to whoever is signed in now.
        if (subscription) resendLanguage(i18n.language).catch(() => undefined)
      })
      .catch(() => alive && setSubscribed(null))
    return () => {
      alive = false
    }
    // The language is read once, on mount; the effect below follows its changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [support])

  // The worker words the push in the language this browser subscribed with; a change re-sends it.
  useEffect(() => {
    if (!subscribed || language.current === i18n.language) return
    language.current = i18n.language
    resendLanguage(i18n.language).catch(() => undefined)
  }, [subscribed, i18n.language])

  if (support === "unsupported" || (support === "available" && subscribed === null)) return null

  const toggle = async () => {
    setBusy(true)
    try {
      if (subscribed) {
        await disablePush()
        setSubscribed(false)
      } else {
        setSubscribed(await enablePush(publicKey, i18n.language))
      }
    } catch (error) {
      // The apiClient has already toasted a server refusal; a browser that refused to subscribe
      // leaves the switch where it was.
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="install-settings push-settings">
      <div className="install-settings-head">
        <span className="section-label">{t("push.settingsTitle")}</span>
        {support === "available" ? (
          <button
            type="button"
            className="install-settings-button"
            aria-pressed={subscribed}
            disabled={busy}
            onClick={toggle}
          >
            {subscribed ? t("push.turnOff") : t("push.turnOn")}
          </button>
        ) : null}
      </div>
      <p className="p-mini install-settings-help">
        {support === "denied" ? t("push.denied") : subscribed ? t("push.on") : t("push.body")}
      </p>
    </div>
  )
}

export default PushSettings
